import { useState, useEffect, useRef } from 'react';

/**
 * Hybrid progress bar: smooth start → real data takeover.
 *
 * Phase 1 (waiting for first result):
 *   Animates 0→15% using an asymptotic curve over ~15s.
 *   Gives the user immediate visual feedback while the AI processes.
 *
 * Phase 2 (real data flowing):
 *   Once realProgress > 0, smoothly transitions to the actual percentage.
 *   Never jumps backwards — always takes the max of smooth and real.
 *
 * Phase 3 (complete):
 *   Snaps to 100%.
 *
 * @param active       Whether the job is actively running
 * @param complete     Whether the job has finished
 * @param realProgress Actual progress percentage (0–100) from completed/total
 */
export function useSmoothProgress(
  active: boolean,
  complete: boolean,
  realProgress = 0,
): number {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    if (complete) {
      cancelAnimationFrame(rafRef.current);
      setValue(100);
      startTimeRef.current = 0;
      return;
    }

    if (!active) {
      cancelAnimationFrame(rafRef.current);
      setValue(0);
      startTimeRef.current = 0;
      return;
    }

    if (!startTimeRef.current) {
      startTimeRef.current = performance.now();
      setValue(0);
    }

    // Phase 1 cap: only go up to 15% on the time-based curve.
    // This feels responsive without overshooting before real data arrives.
    const PHASE1_CAP = 15;
    // τ = 5s → at 5s we're at ~63% of 15% = ~9.5%, at 10s ~13%, at 15s ~14.5%
    const tau = 5000;

    const animate = () => {
      const elapsed = performance.now() - startTimeRef.current;
      const smoothValue = PHASE1_CAP * (1 - Math.exp(-elapsed / tau));

      // Take the maximum: smooth start OR real progress (whichever is higher).
      // This creates a seamless transition — as real data comes in it naturally
      // overtakes the slow phase-1 curve and drives the bar from there.
      const combined = Math.max(smoothValue, realProgress);

      setValue(Math.round(combined * 10) / 10);
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, complete, realProgress]);

  return Math.round(value);
}
